import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import Toggle from '@jetbrains/ring-ui/components/toggle/toggle'
import type {SyntheticEvent} from 'react'
import {Suspense, useCallback, useMemo, useState} from 'react'

import type {GetCleanupRulesArg} from '../../../services/rest'
import {restApi} from '../../../services/rest'
import {ROOT_PROJECT_ID, stringifyId} from '../../../types'
import {resolveHelpURL} from '../../../utils/url'
import HelpLink from '../../common/HelpLink/HelpLink'
import IconButton from '../../common/IconButton/IconButton'
import Incremental from '../../common/Incremental/Incremental'
import Loader from '../../common/Loader/Loader'

import {GetRulesArgContext, ProjectIdContext} from './CleanupProjectPage.contexts'
import type {Policy, Entity, Owner, Props} from './CleanupProjectPage.types'
import EditForm from './EditForm/EditForm'
import Listing from './Listings/Listing'
import {getCleanupProjectNode} from './Listings/Rules/Rules.types'
import StickyHeader from './StickyHeader/StickyHeader'

import styles from './CleanupProjectPage.css'

const linkToHelp = resolveHelpURL('clean-up.html#Clean-Up-ProjectClean-upRules')

function CleanupProjectPage({
  actionPerforming,
  projectInternalId,
  showSubprojects,
  toggleSubprojects,
  projectId,
  readOnlyProjectsKey,
}: Props) {
  const {policiesAreLoaded, policies} = restApi.endpoints.getCleanupPolicies.useQuery(
    {
      projectInternalId,
      includeSubprojects: showSubprojects || projectInternalId !== ROOT_PROJECT_ID,
    },
    {selectFromResult: ({data}) => ({policiesAreLoaded: data != null, policies: data?.data})},
  )
  const [editPolicy] = restApi.endpoints.updateCleanupPolicy.useMutation()
  const [disablePolicy] = restApi.endpoints.updateCleanupPolicyDisableState.useMutation()
  const [resetPolicy] = restApi.endpoints.resetCleanupPolicy.useMutation()
  restApi.endpoints.getDiskUsage.useQuerySubscription(projectId)
  const [formVisible, setFormVisibility] = useState(false)
  const [selectedEntity, setEntity] = useState<Entity | null | undefined>()
  const arg: GetCleanupRulesArg = useMemo(
    () => ({
      holderNode: getCleanupProjectNode({
        internalId: projectInternalId,
        id: projectId,
      }),
      options: {
        getChildrensRules: showSubprojects ? 'withAllInheritors' : 'withOwnBuildTypes',
      },
    }),
    [projectInternalId, projectId, showSubprojects],
  )
  restApi.endpoints.getCleanupRules.useQuerySubscription(arg)
  const hideForm = useCallback(() => {
    setFormVisibility(false)
  }, [])
  const onToggleSubproject = useCallback(
    (e: SyntheticEvent<HTMLInputElement>) => toggleSubprojects(String(e.currentTarget.checked)),
    [toggleSubprojects],
  )
  const ownPolicies =
    policies == null
      ? null
      : policies.policiesPerProject.filter(policy => policy.projectInternalId === projectInternalId)
  const openForm = useCallback(({policy, type, owner}: Entity) => {
    setEntity({
      policy,
      type,
      owner,
    })
    setFormVisibility(true)
  }, [])

  const getProjectPolicies = (
    {
      projectDescription,
      projectPolicies,
      projectExternalId: externalId,
      projectInternalId: internalId,
      templatesPolicies,
      buildTypesPolicies,
    }: Policy,
    index: number,
  ) => {
    const owner: Owner = {
      externalId,
      internalId,
    }
    const firstItem = index === 0
    return (
      <div key={stringifyId(internalId)} className={styles.entity}>
        <StickyHeader
          name={projectPolicies.name}
          description={projectDescription}
          isFirst={firstItem}
        />
        <Suspense>
          <Listing
            policy={projectPolicies}
            type="project"
            owner={owner}
            onPolicyReset={resetPolicy}
            onPolicyClick={openForm}
            onPolicyDisable={(holder, cleanupPoliciesDisabled) =>
              disablePolicy({holder, cleanupPoliciesDisabled})
            }
            readOnlyProjectsKey={readOnlyProjectsKey}
          />
        </Suspense>
        {templatesPolicies.length > 0 &&
          templatesPolicies.map(policy => (
            <Suspense key={stringifyId(policy.internalId)}>
              <Listing
                policy={policy}
                type="template"
                owner={owner}
                onPolicyReset={resetPolicy}
                onPolicyClick={openForm}
                onPolicyDisable={(holder, cleanupPoliciesDisabled) =>
                  disablePolicy({holder, cleanupPoliciesDisabled})
                }
                readOnlyProjectsKey={readOnlyProjectsKey}
              />
            </Suspense>
          ))}
        {buildTypesPolicies.length > 0 &&
          buildTypesPolicies.map(policy => (
            <Suspense key={stringifyId(policy.internalId)}>
              <Listing
                policy={policy}
                type="buildType"
                owner={owner}
                onPolicyReset={resetPolicy}
                onPolicyClick={openForm}
                onPolicyDisable={(holder, cleanupPoliciesDisabled) =>
                  disablePolicy({holder, cleanupPoliciesDisabled})
                }
                readOnlyProjectsKey={readOnlyProjectsKey}
              />
            </Suspense>
          ))}
      </div>
    )
  }

  if (!policiesAreLoaded || policies?.policiesPerProject == null) {
    return <Loader />
  }

  return (
    <GetRulesArgContext.Provider value={arg}>
      <ProjectIdContext.Provider value={projectId}>
        <div className={styles.container}>
          <H1>{'Clean-up Rules'}</H1>
          <p className={styles.description}>
            {`
              Clean-up rules define what data should be\u00a0preserved during the\u00a0clean-up. Rules defined in\u00a0a\u00a0project serve as\u00a0default for\u00a0build\u00a0configurations.
              Base\u00a0rules are\u00a0easy to\u00a0configure and cover general clean-up\u00a0cases. Keep rules can\u00a0be\u00a0used for\u00a0the\u00a0additional fine-tuning.
              The\u00a0build\u00a0data which is\u00a0not\u00a0protected by\u00a0any\u00a0rule is\u00a0deleted during the\u00a0clean-up.
            `}
            <HelpLink
              url={linkToHelp}
              Component={IconButton}
              icon="help"
              className={styles.helpIcon}
            />
          </p>
          {(ownPolicies?.length !== policies.policiesPerProject.length ||
            projectInternalId === ROOT_PROJECT_ID) && (
            <Toggle checked={showSubprojects} onChange={onToggleSubproject}>
              {'Show clean-up rules from subprojects'}
            </Toggle>
          )}
          {showSubprojects && policies?.policiesPerProject && (
            <Incremental>{policies.policiesPerProject.map(getProjectPolicies)}</Incremental>
          )}
          {!showSubprojects && ownPolicies?.map(getProjectPolicies)}
          {formVisible && selectedEntity != null && (
            <EditForm
              policyEntry={selectedEntity.policy}
              type={selectedEntity.type}
              onCancelClick={hideForm}
              onCompleteClick={payload =>
                editPolicy(payload).then(result => {
                  if (!('error' in result)) {
                    hideForm()
                  }
                })
              }
              formVisible={formVisible}
              actionPerforming={actionPerforming}
            />
          )}
        </div>
      </ProjectIdContext.Provider>
    </GetRulesArgContext.Provider>
  )
}

export default CleanupProjectPage
